import React from 'react';

const SCORING_RULE_ORDER = ['EXACT_SCORE', 'SCORE_DIFFERENCE', 'CORRECT_WINNER', 'EXACT_MATCH', 'PARTIAL_MATCH'];

const SCORING_RULE_LABELS = Object.freeze({
  EXACT_SCORE: 'Точный счёт',
  SCORE_DIFFERENCE: 'Точная разница',
  CORRECT_WINNER: 'Победитель',
  EXACT_MATCH: 'Точный исход',
  PARTIAL_MATCH: 'Частичное совпадение',
});

const SCORING_RULE_HINTS = Object.freeze({
  EXACT_SCORE: 'Прогноз полностью совпал с официальным счётом',
  SCORE_DIFFERENCE: 'Разница счёта совпала с официальной',
  CORRECT_WINNER: 'Правильно выбран победитель или ничья',
  EXACT_MATCH: 'Полное совпадение с официальным результатом',
  PARTIAL_MATCH: 'Ставка содержит официальный исход или официальный исход содержит ставку',
});

const DRAW_POLICY_LABELS = Object.freeze({
  DRAW_ALLOWED: 'Ничья разрешена',
  DRAW_FORBIDDEN: 'Ничья запрещена',
  DRAW_WITH_TIE_BREAK_WINNER: 'Ничья с тай-брейком',
});

const DEFAULT_TITLE = 'Правила очков основного исхода';
const EMPTY_MESSAGE = 'Правила начисления очков не настроены';
const POINTS_SUFFIXES = ['очко', 'очка', 'очков'];

const V2_RULE_TYPES = new Set(['EXACT_SCORE', 'SCORE_DIFFERENCE', 'CORRECT_WINNER']);
const LEGACY_RULE_TYPES = new Set(['EXACT_MATCH', 'PARTIAL_MATCH']);

/**
 * Если scoringRules содержат хотя бы одно v2-правило с points > 0,
 * legacy-правила скрываются (иначе — показываются только legacy).
 */
function shouldHideLegacyRules(scoringRules) {
  if (!scoringRules) return false;
  return Object.entries(scoringRules).some(
    ([type, points]) => V2_RULE_TYPES.has(type) && Number(points) > 0,
  );
}

export const getVisibleScoringRules = (scoringRules) => {
  const hideLegacy = shouldHideLegacyRules(scoringRules);
  return SCORING_RULE_ORDER
    .filter((type) => !hideLegacy || !LEGACY_RULE_TYPES.has(type))
    .map((type) => toVisibleScoringRule(type, scoringRules?.[type]))
    .filter(Boolean);
};

export const formatPoints = (points) => `${points} ${pluralizePoints(points)}`;

export const formatScoringRuleShortText = (scoringRules) => {
  const visibleRules = getVisibleScoringRules(scoringRules);
  if (visibleRules.length === 0) return EMPTY_MESSAGE;
  return visibleRules.map((rule) => `${rule.label}: ${formatPoints(rule.points)}`).join(' · ');
};

export default function ScoringRulesSummary({
  scoringRules,
  drawPolicy = null,
  title = DEFAULT_TITLE,
  compact = false,
}) {
  const visibleRules = getVisibleScoringRules(scoringRules);
  const drawPolicyLabel = formatDrawPolicy(drawPolicy);

  if (visibleRules.length === 0) {
    return compact ? null : (
      <div className="bm-scoring-rules bm-scoring-rules--empty">
        <div className="bm-scoring-rules__title">{title}</div>
        <p className="bm-text-secondary bm-text-sm">{EMPTY_MESSAGE}</p>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="bm-scoring-rules bm-scoring-rules--compact" aria-label={title}>
        {visibleRules.map((rule) => (
          <span key={rule.type} className="bm-scoring-rules__chip">
            {rule.label}: <strong>{formatPoints(rule.points)}</strong>
          </span>
        ))}
        {drawPolicyLabel && <span className="bm-scoring-rules__chip">{drawPolicyLabel}</span>}
      </div>
    );
  }

  return (
    <div className="bm-scoring-rules" aria-label={title}>
      <div className="bm-scoring-rules__title">{title}</div>
      {drawPolicyLabel && <div className="bm-scoring-rules__hint">{drawPolicyLabel}</div>}
      <div className="bm-scoring-rules__list">
        {visibleRules.map((rule) => (
          <div key={rule.type} className="bm-scoring-rules__item">
            <div>
              <div className="bm-scoring-rules__label">{rule.label}</div>
              <div className="bm-scoring-rules__hint">{rule.hint}</div>
            </div>
            <strong className="bm-scoring-rules__points">{formatPoints(rule.points)}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatDrawPolicy(drawPolicy) {
  return DRAW_POLICY_LABELS[drawPolicy] || null;
}

function toVisibleScoringRule(type, points) {
  const numericPoints = Number(points);
  if (!Number.isFinite(numericPoints) || numericPoints <= 0) return null;
  return {
    type,
    points: numericPoints,
    label: SCORING_RULE_LABELS[type] || type,
    hint: SCORING_RULE_HINTS[type] || '',
  };
}

function pluralizePoints(points) {
  const absolutePoints = Math.abs(Number(points));
  const lastTwoDigits = absolutePoints % 100;
  const lastDigit = absolutePoints % 10;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return POINTS_SUFFIXES[2];
  if (lastDigit === 1) return POINTS_SUFFIXES[0];
  if (lastDigit >= 2 && lastDigit <= 4) return POINTS_SUFFIXES[1];
  return POINTS_SUFFIXES[2];
}
